import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {boolean | null} Tracks JS-controlled open state so morphs don't reset it. null = defer to server-rendered state. */
  #userExpanded = null;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Restores the disclosure state after a morph overwrites DOM attributes.
   * Without this, any cart update (quantity change etc.) causes morphSection() to
   * re-apply the server-rendered inert attribute, snapping the form shut mid-interaction.
   */
  updatedCallback() {
    super.updatedCallback();
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;

    // If the server re-rendered with the disclosure open (e.g. discount codes applied),
    // the morph already handled it correctly — reset our stored state.
    if (trigger.matches('[aria-expanded="true"]')) {
      this.#userExpanded = null;
      return;
    }

    // If the user had explicitly opened the disclosure, restore that state.
    if (this.#userExpanded === true) {
      trigger.setAttribute('aria-expanded', 'true');
      content.removeAttribute('inert');
      content.classList.add('disclosure-content--open');
    }
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    const newExpanded = !expanded;

    this.#userExpanded = newExpanded;
    trigger.setAttribute('aria-expanded', String(newExpanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    if (newExpanded) {
      content.removeAttribute('inert');
      content.classList.add('disclosure-content--open');
    } else {
      content.setAttribute('inert', '');
      content.classList.remove('disclosure-content--open');
    }
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
